import React from 'react';
import { useMutation } from 'react-query';
import omit from 'lodash/omit';
import { IVenue, IPhoto } from 'btdt-types';
import { FaTrashAlt } from 'react-icons/fa';
import VenueTableRowEditor from './VenueTableRowEditor';
import { IEditedVenue } from './types';
import { validate } from './helpers';
import useApiClient from '../../useApiClient';
import VenueRelationsModalTrigger from './VenueRelationsModalTrigger';
import config from '../../../config';

interface IProps {
  venue: IVenue;
  onUpdated(updatedVenue: IVenue): void;
  autoFocus: keyof IVenue | null;
  onPendingSaveChange(isPendingSave: boolean): void;
}

function getPhotoUrl(url: string): string {
  if (url.startsWith('http')) return url;
  return config.apiBaseUrl.replace(/\/$/, '') + url;
}

const VenueTableRowEdit: React.FC<IProps> = ({ venue, onUpdated, autoFocus, onPendingSaveChange }: IProps) => {
  const { getApiClient } = useApiClient();

  const [editedVenue, setEditedVenue] = React.useState<IEditedVenue>(venue);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = React.useState(false);
  const [photoFile, setPhotoFile] = React.useState<File | null>(null);
  const [photoCaption, setPhotoCaption] = React.useState('');
  const [photoCredit, setPhotoCredit] = React.useState('');
  const [venuePhotos, setVenuePhotos] = React.useState<IPhoto[]>(venue.photos || []);
  const [isUploadingPhoto, setIsUploadingPhoto] = React.useState(false);

  const hasPendingChanges = React.useMemo(() => JSON.stringify(omit(editedVenue, 'relatedVenues', 'photos')) !== JSON.stringify(omit(venue, 'relatedVenues', 'photos')), [editedVenue, venue]);

  React.useEffect(() => {
    onPendingSaveChange(hasPendingChanges);
  }, [hasPendingChanges]);

  const { mutate: save, isLoading } = useMutation(
    async () => {
      const { relatedVenues, photos, ...editedVenueProperties} = editedVenue;
      const apiClient = await getApiClient();
      await apiClient.put(`/api/venue/${venue.id}`, editedVenueProperties);
    },
    {
      onSuccess: () => {
        onUpdated(editedVenue as IVenue);
      },
    },
  );

  const handlePhotoUpload = async (): Promise<void> => {
    if (!photoFile) return;
    setIsUploadingPhoto(true);
    try {
      const apiClient = await getApiClient();
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('venueId', String(venue.id));
      formData.append('caption', photoCaption);
      formData.append('credit', photoCredit);
      const response = await apiClient.post('/api/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newPhoto = response.data.result as IPhoto;
      setVenuePhotos((prev) => [...prev, newPhoto]);
      setPhotoFile(null);
      setPhotoCaption('');
      setPhotoCredit('');
    } catch (error) {
      console.error('Failed to upload photo:', error);
    }
    setIsUploadingPhoto(false);
  };

  const dragIndexRef = React.useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (dropIndex: number) => {
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) return;
    const reordered = [...venuePhotos];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setVenuePhotos(reordered);
    dragIndexRef.current = null;
    try {
      const apiClient = await getApiClient();
      await apiClient.put('/api/photo/reorder', { orderedIds: reordered.map((p) => p.id) });
    } catch (error) {
      console.error('Failed to reorder photos:', error);
    }
  };

  const handlePhotoDelete = async (photoId: number): Promise<void> => {
    try {
      const apiClient = await getApiClient();
      await apiClient.delete(`/api/photo/${photoId}`);
      setVenuePhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (error) {
      console.error('Failed to delete photo:', error);
    }
  };

  const requestDelete = (): void => {
    // eslint-disable-next-line
    if (confirm(`באמת באמת למחוק את ${venue.name}?`)) {
      setEditedVenue({ ...editedVenue, isDeleted: true });
    }
  };

  React.useEffect(() => {
    if (editedVenue.isDeleted) {
      save();
    }
  }, [editedVenue.isDeleted]);

  const { isValid, validationErrors } = validate(editedVenue);

  return (
    <tr>
      <VenueTableRowEditor venue={editedVenue} onChange={setEditedVenue} autoFocus={autoFocus} disabled={isLoading}/>
      <td>
        <div className="VenueTableRowEdit-actions">
          {hasPendingChanges && (
            <button
              type="button"
              onClick={() => save()}
              disabled={isLoading || !isValid}
              title={Object.values(validationErrors).join(', ')}
            >
              שמירה
            </button>
          )}
          <button type="button" onClick={requestDelete} title="מחיקה">
            <FaTrashAlt/>
          </button>
        </div>
      </td>
      <td>
        <VenueRelationsModalTrigger venue={venue}/>
      </td>
      <td>
        <button type="button" onClick={() => setIsPhotoModalOpen(true)}>
          {venuePhotos.length} תמונות
        </button>
        {isPhotoModalOpen && (
          <div className="modal-overlay">
            <div className="venue-relations-modal-content" style={{ width: 500 }}>
              <h3>תמונות — {venue.name}</h3>
              {venuePhotos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {venuePhotos.map((photo, index) => (
                    <div
                      key={photo.id}
                      style={{ position: 'relative', cursor: 'grab' }}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(index)}
                    >
                      <img
                        src={getPhotoUrl(photo.url)}
                        alt={photo.caption}
                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4, pointerEvents: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => handlePhotoDelete(photo.id)}
                        style={{ position: 'absolute', top: -4, right: -4, background: '#c00', color: 'white', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 11, cursor: 'pointer', lineHeight: '16px', padding: 0 }}
                        title="מחיקה"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label>
                  קובץ:
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                    style={{ marginRight: 8 }}
                  />
                </label>
                <label>
                  כיתוב:
                  <input
                    type="text"
                    value={photoCaption}
                    onChange={(e) => setPhotoCaption(e.target.value)}
                    style={{ marginRight: 8, width: '100%' }}
                  />
                </label>
                <label>
                  קרדיט:
                  <input
                    type="text"
                    value={photoCredit}
                    onChange={(e) => setPhotoCredit(e.target.value)}
                    style={{ marginRight: 8, width: '100%' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={handlePhotoUpload}
                  disabled={!photoFile || isUploadingPhoto}
                >
                  {isUploadingPhoto ? 'מעלה...' : 'הוסף תמונה'}
                </button>
              </div>
              <button type="button" onClick={() => setIsPhotoModalOpen(false)} style={{ marginTop: 16 }}>
                סיימתי
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
};

export default VenueTableRowEdit;
